import { useId, useState, type FormEvent } from 'react'
import { newsletterEnabled, subscribe } from '@/site/lib/newsletter'
import PatchworkBlock from './Patchwork'

/** Footer newsletter signup (the Ometria form in lib/newsletter.ts). Renders nothing unless the form is configured. */
export default function NewsletterSignup() {
  const id = useId()
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle')
  const [error, setError] = useState<string | null>(null)

  if (!newsletterEnabled()) return null

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setState('sending')
    const result = await subscribe(email, firstName)
    setState(result.ok ? 'done' : 'idle')
    if (!result.ok) setError(result.error)
  }

  return (
    <section aria-labelledby={`${id}-title`} className="rounded-3xl bg-white/10 p-6 ring-1 ring-white/20 sm:p-8">
      <div className="flex items-start gap-4">
        <PatchworkBlock block="star" color="bg-qt-badge" className="hidden size-12 shrink-0 sm:block" />
        <div className="min-w-0 flex-1">
          <h2 id={`${id}-title`} className="text-xl font-extrabold">
            Get new tutorials by email
          </h2>
          <p className="mt-1 text-sm font-medium text-white/85">New projects, patterns and techniques from Missouri Star, straight to your inbox.</p>

          {state === 'done' ? (
            <p role="status" className="mt-4 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-qt-ink">
              You’re in! Watch your inbox for the next new tutorial.
            </p>
          ) : (
            <form onSubmit={submit} noValidate className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="grid flex-1 gap-3 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                <div>
                  <label htmlFor={`${id}-first`} className="sr-only">
                    First name (optional)
                  </label>
                  <input
                    id={`${id}-first`}
                    autoComplete="given-name"
                    placeholder="First name (optional)"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="h-12 w-full rounded-full border-0 bg-white px-5 text-base font-medium text-qt-ink placeholder:text-qt-muted focus:ring-4 focus:ring-qt-badge focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor={`${id}-email`} className="sr-only">
                    Email address
                  </label>
                  <input
                    id={`${id}-email`}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${id}-error` : undefined}
                    className="h-12 w-full rounded-full border-0 bg-white px-5 text-base font-medium text-qt-ink placeholder:text-qt-muted focus:ring-4 focus:ring-qt-badge focus:outline-none"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={state === 'sending'}
                className="h-12 shrink-0 rounded-full bg-qt-badge px-6 text-base font-extrabold whitespace-nowrap text-qt-badge-ink transition hover:brightness-105 disabled:opacity-70"
              >
                {state === 'sending' ? 'Signing up…' : 'Sign me up'}
              </button>
            </form>
          )}
          {error && (
            <p id={`${id}-error`} role="alert" className="mt-2 text-sm font-bold text-white">
              {error}
            </p>
          )}
          <p className="mt-3 text-xs font-medium text-white/70">You can unsubscribe at any time from the link in every email.</p>
        </div>
      </div>
    </section>
  )
}
